// This component shows various reports based on the data available.
// The reports it shows are:
// Daily summary, including total income and expenses, by type and compared to goals and previous periods.
// Weekly summary, including total income and expenses, by type and compared to goals and previous periods.
// Monthly summary, including total income and expenses, by type and compared to goals and previous periods.
import { useEffect, useState } from 'react'
import { useGodSheets } from './godsheets-context'
import { useNavigate } from 'react-router'
import BarLoader from 'react-spinners/BarLoader'
import { CircularProgressbar } from 'react-circular-progressbar'

import { IMAGES } from '../logic/expense-types'

export default function Reports() {
  const [report, setReport] = useState('daily')
  const { godSheets } = useGodSheets()
  const current = godSheets[new Date().getFullYear()]
  
  const navigate = useNavigate()

  useEffect(() => {
    if (!current) return navigate('/')
  }, [current, navigate])

  if (!godSheets || !current) return <div className='loader'><BarLoader color='#2d3f47' loading={true} size={100} /></div>

  return <div className='reports'>
    <div className='title'>report</div>
    <div className='buttons'>
      {['daily', 'weekly', 'monthly'].map(currentReport =>
        <div key={currentReport} className={`inline tiny button ${report === currentReport ? 'primary' : ''}`} onClick={() => setReport(currentReport)}>{currentReport}</div>)}
    </div>
    <div className='report-content' style={{ margin: '1rem 0', flexGrow: 1 }}>
      {REPORT_COMPONENTS[report]({ data: current })}
    </div>
  </div>
}

function DailyReport({ data }) {
  const [shift, setShift] = useState(0) // 0 for today, -1 for yesterday, etc.
  const day = new Date()
  day.setDate(day.getDate() + shift)
  const dailyExpenses = data.expenses.filter(expense => {
    const expenseDate = new Date(expense.date)
    return expenseDate.getDate() === day.getDate() &&
           expenseDate.getMonth() === day.getMonth() &&
           expenseDate.getFullYear() === day.getFullYear()
  })
  console.log(dailyExpenses)
  const totalDailyExpenses = dailyExpenses.reduce((sum, expense) => sum + expense['amount (NIS)'], 0)
  const dailyExpensesGoal = 100 // Placeholder for daily expenses goal
  const ratio = totalDailyExpenses / dailyExpensesGoal
  const color = ratio > 1 ? '#ec644b' : (ratio > 0.75 ? '#f39c12' : (ratio > 0.5 ? '#080909ff' : '#2ecc71'))
  
  return <div className='daily-report'>
    <div className='shift-buttons buttons'>
      <div className='inline tiny button' onClick={() => setShift(shift - 1)}>&lt;</div>
      <div className='inline tiny button' onClick={() => setShift(0)}>reset</div>
      <div className='inline tiny button' onClick={() => setShift(shift + 1)}>&gt;</div>
    </div>
    <div className='text' style={{ textAlign: 'center', margin: '0.5rem 0' }}>
      {day.getDate()}/{day.getMonth() + 1}/{day.getFullYear()}
    </div>
    <CircularProgressbar value={100 * ratio} text={`${roundToDigits(totalDailyExpenses, 2)}₪`} styles={{ path: { stroke: color }, text: { fill: color, fontFamily: 'var(--font)' } }} />
    <div className='events'>
      {dailyExpenses.length === 0 && <div style={{ textAlign: 'center', marginTop: '1rem' }}>No expenses recorded for this day.</div>}
      {dailyExpenses.map((expense, index) =>
        <div key={index} className='event'>
          <div className='name'>{expense['title']}</div>
          <div className='amount'>{expense['amount (NIS)']}₪</div>
          <div className='type'>{IMAGES[expense['type']] || IMAGES.default}</div>
        </div>
      )}
    </div>
  </div>
}

function WeeklyReport({ data }) {
  const [shift, setShift] = useState(0) // 0 for current week, -1 for last week, etc.
  const currentDate = new Date()
  currentDate.setDate(currentDate.getDate() + (shift * 7))
  const firstDayOfWeek = new Date(currentDate.setDate(currentDate.getDate() - currentDate.getDay()))
  const lastDayOfWeek = new Date(currentDate.setDate(firstDayOfWeek.getDate() + 6))
  const currentWeekExpenses = data.expenses.filter(expense => {
    const expenseDate = new Date(expense.date)
    return expenseDate >= firstDayOfWeek && expenseDate <= lastDayOfWeek
  }).reduce((sum, expense) => sum + expense['amount (NIS)'], 0)
  const weeklyExpensesGoal = 7 * 100 // Placeholder for weekly expenses goal
  const ratio = currentWeekExpenses / weeklyExpensesGoal
  const color = ratio > 1 ? '#ec644b' : (ratio > 0.75 ? '#f39c12' : (ratio > 0.5 ? '#080909ff' : '#2ecc71'))
  return <div className='weekly-report'>
    <div className='shift-buttons buttons'>
      <div className='inline tiny button' onClick={() => setShift(shift - 1)}>&lt;</div>
      <div className='inline tiny button' onClick={() => setShift(0)}>reset</div>
      <div className='inline tiny button' onClick={() => setShift(shift + 1)}>&gt;</div>
    </div>
    <div className='text' style={{ textAlign: 'center', margin: '0.5rem 0' }}>
      {firstDayOfWeek.getDate()}/{firstDayOfWeek.getMonth() + 1}/{firstDayOfWeek.getFullYear()} - {lastDayOfWeek.getDate()}/{lastDayOfWeek.getMonth() + 1}/{lastDayOfWeek.getFullYear()}
    </div>
    <CircularProgressbar value={100 * ratio} text={`${roundToDigits(currentWeekExpenses, 2)}₪`} styles={{ path: { stroke: color }, text: { fill: color, fontFamily: 'var(--font)' } }} />
  </div>
}

function MonthlyReport({ data }) {
  const [shift, setShift] = useState(0) // 0 for current month, -1 for last month, etc.
  const currentMonth = new Date().getMonth() + 1 + shift
  const monthlyExpenses = data.expenses.filter(expense => {
    const expenseDate = new Date(expense.date)
    return (expenseDate.getMonth() + 1) === currentMonth && expenseDate.getFullYear() === new Date().getFullYear()
  }).reduce((sum, expense) => sum + expense['amount (NIS)'], 0)
  const monthlyExpensesGoal = 30 * 100 // Placeholder for monthly expenses goal
  const ratio = monthlyExpenses / monthlyExpensesGoal
  const color = ratio > 1 ? '#ec644b' : (ratio > 0.75 ? '#f39c12' : (ratio > 0.5 ? '#080909ff' : '#2ecc71'))
  return <div className='monthly-report'>
    <div className='shift-buttons buttons'>
      <div className='inline tiny button' onClick={() => setShift(shift - 1)}>&lt;</div>
      <div className='inline tiny button' onClick={() => setShift(0)}>reset</div>
      <div className='inline tiny button' onClick={() => setShift(shift + 1)}>&gt;</div>
    </div>
    <div className='text' style={{ textAlign: 'center', margin: '0.5rem 0' }}>
      {MONTHS[currentMonth - 1]} {new Date().getFullYear()}
    </div>
    <CircularProgressbar value={100 * ratio} text={`${roundToDigits(monthlyExpenses, 2)}₪`} styles={{ path: { stroke: color }, text: { fill: color, fontFamily: 'var(--font)' } }} />
  </div>
}

const REPORT_COMPONENTS = {
  daily: DailyReport,
  weekly: WeeklyReport,
  monthly: MonthlyReport
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

function roundToDigits(number, digits) {
  const factor = Math.pow(10, digits)
  return Math.round(number * factor) / factor
}